import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type { UpdateControlTestOversightResultApiMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateControlTestOversightResultApiDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type UpdateControlTestOversightResultWrapperInput = {
  Id: string;
  // ParentControlId + OriginalTimestamp are both required by the legacy Apollo
  // `UpdateTestResultInput`. Since COI-637 the tRPC path honours
  // OriginalTimestamp too: it is pinned in the data-layer's WHERE and a stale
  // save comes back as a 409, the ModifiedSinceLastView parity.
  ParentControlId: string;
  OriginalTimestamp?: string | null;
  Title?: string | null;
  TestType?: TestType | null;
  Description?: string | null;
  DesignEffectiveness?: number | null;
  PerformanceEffectiveness?: number | null;
  OverallEffectiveness?: number | null;
  Submitter?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useUpdateControlTestOversightResult = createMutationHook({
  name: 'updateControlTestOversightResult',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.assessment.updateControlTestOversightResult.mutationOptions({
      onSuccess,
    }),
  // COI-637: forward the whole input INCLUDING OriginalTimestamp — the tRPC
  // path now locks on it. It used to be stripped here.
  mapInput: (input: UpdateControlTestOversightResultWrapperInput) => input,
  invalidate: (trpc) => [
    // Apollo parity: evictField(cache, 'control_test_oversight_result')
    trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
    trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
    trpc.frontend.assessment.oversightResultById.pathKey(),
    // Apollo parity: evictField(cache, 'oversight_assessment')
    trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
    trpc.frontend.assessment.oversightActivityRegister.queryKey(),
    trpc.frontend.assessment.oversightActivityCount.queryKey(),
  ],
  mapResponse: (result: {
    Id: string;
  }): UpdateControlTestOversightResultApiMutation => ({
    updateControlTestOversightResultApi: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateControlTestOversightResultApiDocument,
  graphqlVariables: (input: UpdateControlTestOversightResultWrapperInput) => ({
    object: {
      Id: input.Id,
      ParentControlId: input.ParentControlId,
      OriginalTimestamp: input.OriginalTimestamp,
      Title: input.Title,
      TestType: input.TestType,
      Description: input.Description,
      DesignEffectiveness: input.DesignEffectiveness,
      PerformanceEffectiveness: input.PerformanceEffectiveness,
      OverallEffectiveness: input.OverallEffectiveness,
      Submitter: input.Submitter,
      TestDate: input.TestDate,
      CustomAttributeData: input.CustomAttributeData ?? undefined,
    },
  }),
  evictFields: [
    'control_test_oversight_result',
    'control',
    'oversight_assessment',
  ],
});
